import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Container,
  Autocomplete,
  Paper,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Helmet } from 'react-helmet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  getAuth,
  browserLocalPersistence,
  setPersistence
} from 'firebase/auth';

// Components
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import Sidebar from '../../components/Sidebar/Sidebar';
import CustomModal from '../../components/CustomModal';
import CustomNoResponseModal from '../../components/CustomNoResponseModal';

// Assets
import cleverIcon from '../../../assets/img/Clever-icon.png';
import classlinkIcon from '../../../assets/img/classlink-icon.png';

// Firebase and utilities
import { authen } from '../../firebase/firebase';
import { fetchCookieUsers } from '../../Utils/firebase-services';
import RemoteErrorLog from '../../Utils/api';
import { environment } from '../../../environments/environment';

// Styles
import '../../scss/Form.scss';

interface School {
  name: string;
  address: string;
  city: string;
  school_id: string;
  state: string;
  displayName: string;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3, 4),
  borderRadius: theme.spacing(2.5),
  border: '1px solid #e2e8f0',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  boxShadow: '0 2px 15px rgba(0, 0, 0, 0.06), 0 1px 8px rgba(0, 0, 0, 0.04)',
  marginBottom: theme.spacing(2.5),
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.2s ease',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #2A9235, #4CAF50)',
  },

  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 10px rgba(0, 0, 0, 0.05)',
    transform: 'translateY(-1px)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: '#ffffff',
    transition: 'all 0.2s ease',
    fontSize: '0.95rem',

    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2A9235',
      }
    },

    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2A9235',
        borderWidth: '2px',
      }
    }
  },

  '& .MuiInputLabel-root': {
    fontSize: '0.95rem',
    '&.Mui-focused': {
      color: '#2A9235',
    }
  },

  '& .MuiFormHelperText-root': {
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.5),
  }
}));

const StyledConnectButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '60px',
  padding: theme.spacing(2, 3),
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  gap: theme.spacing(1.5),
  borderRadius: theme.spacing(4),
  border: '2px solid #e2e8f0',
  background: '#ffffff',
  color: '#1a1a1a',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)',
  
  '&:hover:not(:disabled)': {
    borderColor: '#2A9235',
    background: '#ffffff',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 20px rgba(42, 146, 53, 0.15)',
    
    '& .platform-logo': {
      transform: 'scale(1.05)',
    }
  },

  '&:disabled': {
    opacity: 0.5,
    background: '#f5f5f5',
    cursor: 'not-allowed',
  },

  '& .platform-logo': {
    marginRight: theme.spacing(1),
    height: 32,
    width: 32,
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2.5),
  padding: theme.spacing(1.75, 4),
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
  color: 'white',
  border: 'none',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 3px 15px rgba(42, 146, 53, 0.2)',

  '&:hover:not(:disabled)': {
    background: 'linear-gradient(135deg, #1e7a2b 0%, #43a047 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 5px 20px rgba(42, 146, 53, 0.25)',
  },

  '&:disabled': {
    background: '#cccccc',
    color: '#666666',
    cursor: 'not-allowed',
  }
}));

const BackButton = styled(Button)(({ theme }) => ({
  color: '#2A9235',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(0.75, 1.5),
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.2s ease',
  fontSize: '0.9rem',
  '&:hover': {
    backgroundColor: 'rgba(42, 146, 53, 0.08)',
    transform: 'translateX(-2px)',
  }
}));

const SchoolLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolId } = useParams<{ schoolId?: string }>();

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [schoolName, setSchoolName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [schools, setSchools] = useState<School[]>([]);
  const [schoolError, setSchoolError] = useState<string | null>(null);
  const [isLoadingSchools, setIsLoadingSchools] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isQuestionModalVisible, setIsQuestionModalVisible] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate('/login');
  };

  const handleToggleShowPassword = () => setShowPassword((s) => !s);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // Helper function to handle redirect after successful login
  const handleSuccessfulLogin = () => {
    const state = location.state as any;
    if (state?.redirectTo === '/profile' && state?.openMembershipTab) {
      navigate('/profile', {
        state: {
          openMembershipTab: true,
          tabIndex: state.tabIndex || 3
        }
      });
    } else {
      navigate('/home');
    }
  };

  // Load schools and set initial values
  useEffect(() => {
    const fetchSchoolsData = async () => {
      if (schoolId && schools.length === 0) {
        try {
          setIsLoadingSchools(true);
          setSchoolError(null);
          const response = await axios.get(`${environment.backendUrl}api/schools`);
          
          if (response.data.status === 'success') {
            setSchools(response.data.schools);
            const school = response.data.schools.find((s: School) => s.school_id === schoolId);
            if (school) {
              setSchoolName(school.name);
            } else {
              setSchoolError('Invalid school ID');
            }
          } else {
            throw new Error('Failed to fetch schools');
          }
        } catch (error) {
          RemoteErrorLog(error, 'SchoolLogin.tsx');
          setSchoolError('Unable to load schools. Please try again later.');
        } finally {
          setIsLoadingSchools(false);
        }
      }
    };

    fetchSchoolsData();
  }, [schoolId, schools.length]);

  // Handle school field click
  const handleSchoolFieldClick = async () => {
    if (schools.length === 0 && !isLoadingSchools) {
      try {
        setIsLoadingSchools(true);
        setSchoolError(null);
        const response = await axios.get(`${environment.backendUrl}api/schools`);
        
        if (response.data.status === 'success') {
          setSchools(response.data.schools);
        } else {
          throw new Error('Failed to fetch schools');
        }
      } catch (error) {
        RemoteErrorLog(error, 'SchoolLogin.tsx');
        setSchoolError('Unable to load schools. Please try again later.');
      } finally {
        setIsLoadingSchools(false);
      }
    }
  };

  // Clever Sign In
  const cleverSignIn = async () => {
    try {
      setIsLoading(true);
      const [authResponse] = await Promise.all([
        axios.get(`${environment.backendUrl}oauth/authorize?channel=LIWC`),
        setPersistence(getAuth(), browserLocalPersistence),
      ]);

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authResponse.data.login_url,
        'Clever Login',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        setIsLoading(false);
        throw new Error('Popup blocked. Please enable popups and try again.');
      }

      const userInfo = await new Promise<{ user: any }>((resolve, reject) => {
        const popupCheckInterval = setInterval(() => {
          if (popup.closed) {
            clearInterval(popupCheckInterval);
            window.removeEventListener('message', handleMessage);
            setIsLoading(false);
            reject(new Error('Login cancelled'));
          }
        }, 500);

        const handleMessage = async (event: MessageEvent) => {
          if (event.data.error) {
            clearInterval(popupCheckInterval);
            reject(new Error(event.data.message || 'Error during Clever login'));
            return;
          }

          if (event.data.token && event.data.user_id) {
            try {
              clearInterval(popupCheckInterval);
              const auth = getAuth();
              const credential = await signInWithCustomToken(auth, event.data.token);
              resolve({ user: credential.user });
            } catch (error) {
              reject(error);
            } finally {
              popup.close();
              window.removeEventListener('message', handleMessage);
            }
          }
        };

        window.addEventListener('message', handleMessage);

        setTimeout(() => {
          clearInterval(popupCheckInterval);
          window.removeEventListener('message', handleMessage);
          popup.close();
          setIsLoading(false);
          reject(new Error('Login timed out. Please try again.'));
        }, 300000);
      });

      localStorage.setItem('userData', JSON.stringify(userInfo.user));
      const { uid } = userInfo.user;
      const userCookiePreference = await fetchCookieUsers(uid);

      if (userCookiePreference === 'Error') {
        setApiErrorMessage("We're currently experiencing some issues. Please try again later.");
        setIsQuestionModalVisible(true);
        localStorage.removeItem('userData');
      } else {
        handleSuccessfulLogin();
      }
    } catch (error: any) {
      setIsLoading(false);
      if (error.message === 'Login cancelled') return;
      
      RemoteErrorLog(error, 'SchoolLogin.tsx');
      if (error.message.includes("We can't find your account")) {
        setApiErrorMessage(error.message);
        setIsQuestionModalVisible(true);
      } else {
        setApiErrorMessage('Failed to authenticate with Clever. Please try again.');
        setIsQuestionModalVisible(true);
      }
    }
  };

  // ClassLink Sign In
  const classlinkSignIn = async () => {
    try {
      setIsLoading(true);
      const [authResponse] = await Promise.all([
        axios.get(`${environment.backendUrl}classlink/authorize`, { timeout: 8000 }),
        setPersistence(getAuth(), browserLocalPersistence),
      ]);

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authResponse.data.login_url,
        'ClassLink Login',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        setIsLoading(false);
        throw new Error('Popup blocked. Please enable popups and try again.');
      }

      const userInfo = await new Promise<{ user: any }>((resolve, reject) => {
        const popupCheckInterval = setInterval(() => {
          if (popup.closed) {
            clearInterval(popupCheckInterval);
            window.removeEventListener('message', handleMessage);
            setIsLoading(false);
            reject(new Error('Login cancelled'));
          }
        }, 500);

        const handleMessage = async (event: MessageEvent) => {
          if (event.data.error) {
            clearInterval(popupCheckInterval);
            reject(new Error(event.data.message || 'Error during ClassLink login'));
            return;
          }

          if (event.data.token && event.data.user_id) {
            try {
              clearInterval(popupCheckInterval);
              const auth = getAuth();
              const credential = await signInWithCustomToken(auth, event.data.token);
              resolve({ user: credential.user });
            } catch (error) {
              reject(error);
            } finally {
              popup.close();
              window.removeEventListener('message', handleMessage);
            }
          }
        };

        window.addEventListener('message', handleMessage);

        setTimeout(() => {
          clearInterval(popupCheckInterval);
          window.removeEventListener('message', handleMessage);
          popup.close();
          setIsLoading(false);
          reject(new Error('Login timed out. Please try again.'));
        }, 300000);
      });

      localStorage.setItem('userData', JSON.stringify(userInfo.user));
      const { uid } = userInfo.user;
      const userCookiePreference = await fetchCookieUsers(uid);

      if (userCookiePreference === 'Error') {
        setApiErrorMessage("We're currently experiencing some issues. Please try again later.");
        setIsQuestionModalVisible(true);
        localStorage.removeItem('userData');
      } else {
        handleSuccessfulLogin();
      }
    } catch (error: any) {
      setIsLoading(false);
      if (error.message === 'Login cancelled') return;
      
      RemoteErrorLog(error, 'SchoolLogin.tsx');
      if (error.message.includes("We can't find your account")) {
        setApiErrorMessage(error.message);
        setIsQuestionModalVisible(true);
      } else {
        setApiErrorMessage('Failed to authenticate with ClassLink. Please try again.');
        setIsQuestionModalVisible(true);
      }
    }
  };

  // School Username/Password Login
  const handleSchoolLogin = async () => {
    setIsLoading(true);
    try {
      let loginEmail;

      // Verify school user
      const verificationResponse = await axios.post(
        `${environment.backendUrl}api/verify-school-user`,
        { schoolName, username }
      );

      if (verificationResponse.data.status === 'success') {
        loginEmail = verificationResponse.data.email;
      } else {
        throw new Error('Invalid school or username');
      }

      // Sign in with email and password
      const userCredential = await signInWithEmailAndPassword(authen, loginEmail, password);
      const user = userCredential.user;

      if (user.emailVerified) {
        await user.getIdToken(true);
        const idTokenResult = await user.getIdTokenResult();
        localStorage.setItem('userData', JSON.stringify(userCredential.user));
        
        const userCookiePreference = await fetchCookieUsers(user.uid);

        if (userCookiePreference === 'Error') {
          setApiErrorMessage("We're currently experiencing some issues. Please try again later.");
          setIsQuestionModalVisible(true);
          localStorage.removeItem('userData');
        } else {
          if (idTokenResult.claims.indian_school_admin) {
            navigate('/school-admin-dashboard');
          } else {
            handleSuccessfulLogin();
          }
        }
      } else {
        setApiErrorMessage('Please verify your email address before logging in.');
        setIsQuestionModalVisible(true);
      }
    } catch (error: any) {
      RemoteErrorLog(error, 'SchoolLogin.tsx');
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.error || 'Invalid school or username';
        setApiErrorMessage(errorMessage);
        setIsQuestionModalVisible(true);
      } else {
        setApiErrorMessage('The email or password entered is incorrect. Please try again.');
        setIsQuestionModalVisible(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validatePassword = (text: string) => {
    if (text.length < 6) {
      setErrorMessage('The password must be at least 6 characters long.');
    } else {
      setErrorMessage('');
    }
  };

  const isFormValid = () => {
    return schoolName && username && password.length >= 6;
  };

  // Modal handlers
  const hideModal = () => {
    setIsModalVisible(false);
    setIsQuestionModalVisible(false);
  };

  return (
    <>
      <Helmet>
        <title>School Login - Spelling Hero</title>
        <meta name="description" content="School and classroom login for Spelling Hero. Access with Clever, ClassLink, or school credentials to manage student accounts and spelling activities." />
        <meta name="keywords" content="school login, clever login, classlink login, student login, teacher login, classroom management" />
      </Helmet>

      <header>
        <Navbar currentPage="login" />
      </header>

      <main className="main-container" style={{ paddingTop: 0 }}>
        <section className="quiz-signup">
          <Box sx={{ padding: 0 }}>
            <Grid container spacing={2}>
              <Grid
                item
                lg={5}
                xs={12}
                sm={12}
                sx={{ paddingLeft: 0 }}
                className="sidebar-top-spacing"
              >
                <Sidebar />
              </Grid>

              <Grid item xs={12} sm={12} lg={7} xl={7}>
                <Box sx={{
                  position: 'relative',
                  paddingLeft: { xs: 2, sm: 3, md: 4 },
                  paddingRight: { xs: 2, sm: 3, md: 4 },
                  paddingTop: { xs: 1, md: 0 },
                  paddingBottom: 4,
                  display: 'flex',
                  alignItems: { xs: 'center', md: 'flex-start' },
                  minHeight: { xs: 'auto', md: 'calc(100vh - 180px)' },
                  marginTop: 0
                   }}>
                     <Box sx={{
                       position: { xs: 'relative', md: 'absolute' },
                       top: { md: 24 },
                       left: { md: 24 },
                       zIndex: 1,
                       mb: { xs: 2, md: 0 },
                       display: { xs: 'none', md: 'flex' },
                       justifyContent: { md: 'flex-start' }
                     }}>
                       <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
                         Back
                       </BackButton>
                     </Box>

                    <Box sx={{ width: '100%', maxWidth: 920, mx: 'auto', pt: 0 }}>
                       <Container maxWidth="lg" sx={{ py: 1, pt: 2, px: { xs: 2, md: 4 } }}>
                        <Box sx={{ textAlign: 'center', mb: 1 }}>
                          <Typography 
                            variant="h2" 
                            component="h2" 
                            sx={{ 
                              mb: 1.5, 
                              fontWeight: 700,
                              background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              color: 'text.secondary',
                              fontSize: { xs: '1.75rem', md: '2.25rem' }
                            }}
                          >
                            School & Classroom Login
                          </Typography>
                          
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              color: 'text.secondary',
                              fontWeight: 400,
                              maxWidth: 500,
                              mx: 'auto',
                              lineHeight: 1.4,
                              mb: 2,
                              fontSize: { xs: '0.95rem', md: '1.1rem' }
                            }}
                          >
                            Log in with your school's provider or use your school credentials
                          </Typography>

                          <Box 
                            sx={{ 
                              width: 50, 
                              height: 3, 
                              background: 'linear-gradient(90deg, #2A9235, #4CAF50)',
                              borderRadius: 2,
                              mx: 'auto'
                            }} 
                          />
                        </Box>

                    {/* SSO Login Options */}
                    <StyledPaper>
                      {/* ...existing code for SSO buttons... */}
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 2, 
                          fontWeight: 600, 
                          color: 'text.secondary',
                          textAlign: 'center',
                          fontSize: '1.1rem'
                        }}
                      >
                        Log in with your school's provider
                      </Typography>
                      
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12}>
                          <StyledConnectButton
                            onClick={cleverSignIn}
                            disabled={isLoading}
                          >
                            <img 
                              src={cleverIcon} 
                              alt="Clever" 
                              className="platform-logo"
                              style={{ transition: 'transform 0.3s ease' }} 
                            />
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              Login with Clever
                            </Typography>
                          </StyledConnectButton>
                        </Grid>

                        <Grid item xs={12}>
                          <StyledConnectButton
                            onClick={classlinkSignIn}
                            disabled={isLoading}
                          >
                            <img 
                              src={classlinkIcon} 
                              alt="ClassLink" 
                              className="platform-logo"
                              style={{ transition: 'transform 0.3s ease' }} 
                            />
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              Login with ClassLink
                            </Typography>
                          </StyledConnectButton>
                        </Grid>
                      </Grid>
                    </StyledPaper>

                    {/* Username/Password Login */}
                    <StyledPaper>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 2, 
                          fontWeight: 600, 
                          color: 'text.secondary',
                          textAlign: 'center',
                          fontSize: '1.1rem'
                        }}
                      >
                        or continue with username
                      </Typography>
                      {/* ...existing code for username/password fields and button... */}
                      <Grid container spacing={2.5}>
                        {/* ...existing grid items for form fields... */}
                        <Grid item xs={12}>
                          <Autocomplete
                            options={schools}
                            loading={isLoadingSchools}
                            getOptionLabel={(option: School) =>
                              `${option.name}, ${option.address}, ${option.city}, ${option.state}`
                            }
                            value={schools.find((school: School) => school.name === schoolName) || null}
                            onChange={(event, newValue: School | null) => {
                              setSchoolName(newValue ? newValue.name : '');
                            }}
                            onOpen={handleSchoolFieldClick}
                            isOptionEqualToValue={(option, value) => option.name === value.name}
                            renderInput={(params) => (
                              <StyledTextField
                                {...params}
                                label="School Name *"
                                placeholder="Search and select your school"
                                error={!!schoolError}
                                helperText={schoolError}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: <SchoolIcon sx={{ color: '#2A9235', mr: 1.5 }} />,
                                  endAdornment: (
                                    <>
                                      {isLoadingSchools ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label="Username *"
                            placeholder="Enter your username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            InputProps={{
                              startAdornment: <PersonIcon sx={{ color: '#2A9235', mr: 1.5 }} />
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label="Password *"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => {
                              setPassword(e.target.value);
                              validatePassword(e.target.value);
                            }}
                            error={!!errorMessage}
                            helperText={errorMessage}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockIcon sx={{ color: '#2A9235', mr: 1.5 }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    onClick={handleToggleShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                    size="small"
                                  >
                                    {showPassword ? (
                                      <VisibilityOff sx={{ color: '#2A9235' }} />
                                    ) : (
                                      <Visibility sx={{ color: '#2A9235' }} />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 3, mb: 1, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                          Need login help? Contact your teacher or school administrator.
                        </Typography>
                      </Box>

                      <StyledButton
                        fullWidth
                        size="large"
                        onClick={handleSchoolLogin}
                        disabled={!isFormValid() || isLoading}
                        sx={{ mt: 2 }}
                      >
                        {isLoading ? (
                          <>
                            <CircularProgress size={18} sx={{ mr: 2, color: 'white' }} />
                            Logging In...
                          </>
                        ) : (
                          'Log In'
                        )}
                      </StyledButton>
                    </StyledPaper>
                    </Container>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </section>
      </main>

      <Footer />

      {/* Modals */}
      <CustomModal
        isModalVisible={isModalVisible}
        onPress={hideModal}
        buttonText="OK"
        modalTitle="Email Verification Required"
        text="Please verify your email address before logging in."
        icon={true}
      />

      <CustomNoResponseModal
        isModalVisible={isQuestionModalVisible}
        onPress={hideModal}
        buttonText="OK"
        modalTitle="Error"
        text={apiErrorMessage}
      />
    </>
  );
};

export default SchoolLogin;
